import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { useProviderAuth } from '@/hooks/auth'
import { useCompleteOnboarding } from '@/hooks/useOnboardingProgress'
import { useVisitTypes } from '@/hooks/useVisitTypes'
import { practiceProfileStepDataSchema } from '@/schemas/onboarding-practice-profile'
import { stripeConnectStepDataSchema } from '@/schemas/onboarding-stripe'
import type { StripeAccountStatus } from '@/schemas/onboarding-stripe'

const STEP_INDEX = 9

interface ChecklistItemProps {
  complete: boolean
  label: string
  detail?: string
  warning?: boolean
  action?: { label: string; onClick: () => void }
}

function ChecklistItem({
  complete,
  label,
  detail,
  warning = false,
  action,
}: ChecklistItemProps) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ' +
          (warning
            ? 'bg-amber-100 text-amber-700'
            : complete
              ? 'bg-green-100 text-green-700'
              : 'bg-slate-100 text-slate-400')
        }
        aria-hidden
      >
        {warning ? (
          <AlertTriangle className="h-3 w-3" />
        ) : complete ? (
          <Check className="h-3 w-3" />
        ) : null}
      </span>
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {detail && <p className="text-xs text-slate-500">{detail}</p>}
      </div>
      {action && (
        <Button type="button" size="sm" variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  )
}

function stripeDescription(status: StripeAccountStatus): {
  detail: string
  warning: boolean
  complete: boolean
} {
  switch (status) {
    case 'verified':
      return { detail: 'Verified — ready to accept payments.', warning: false, complete: true }
    case 'pending':
      return {
        detail: 'Stripe is reviewing your information. You can go live and continue in parallel.',
        warning: true,
        complete: false,
      }
    case 'restricted':
      return {
        detail: 'Additional information is required before you can accept payments.',
        warning: true,
        complete: false,
      }
    case 'not_connected':
    default:
      return {
        detail: 'Stripe is not connected. Connect to accept patient payments.',
        warning: true,
        complete: false,
      }
  }
}

export function GoLiveStep() {
  const { state, goToStep, markCompleted } = useOnboarding()
  const { provider, refreshProvider } = useProviderAuth()
  const { data: visitTypes } = useVisitTypes()
  const completeOnboarding = useCompleteOnboarding()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)

  const practiceParsed = practiceProfileStepDataSchema.safeParse(state.stepData[4])
  const slug = practiceParsed.success ? practiceParsed.data.slug : undefined
  const practiceUrl = slug ? `determa.co/p/${slug}` : null

  const stripeParsed = stripeConnectStepDataSchema.safeParse(state.stepData[3])
  const stripeStatus: StripeAccountStatus = stripeParsed.success
    ? stripeParsed.data.status
    : 'not_connected'
  const stripeInfo = stripeDescription(stripeStatus)

  const activeVisitTypeCount = (visitTypes ?? []).filter((vt) => vt.is_active).length

  const accountDone = Boolean(provider)
  const credentialsDone = state.completedSteps.has(1)
  const agreementsDone = state.completedSteps.has(2)
  const practiceDone = state.completedSteps.has(4)
  const providerProfileDone = state.completedSteps.has(5)
  const visitTypesDone = activeVisitTypeCount > 0
  const availabilityDone = state.completedSteps.has(7)
  const protocolDone = state.completedSteps.has(8)

  const handleGoLive = async () => {
    try {
      await completeOnboarding.mutateAsync()
    } catch {
      toast.error('Could not complete onboarding. Please try again.')
      return
    }
    markCompleted(STEP_INDEX)
    await refreshProvider()
    navigate('/visits', { replace: true })
  }

  const handleCopy = async () => {
    if (!practiceUrl) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(practiceUrl)
      } else {
        // Fallback for contexts where navigator.clipboard is unavailable
        // (older browsers, non-secure origins, some embedded webviews).
        const textarea = document.createElement('textarea')
        textarea.value = practiceUrl
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        const succeeded = document.execCommand('copy')
        document.body.removeChild(textarea)
        if (!succeeded) throw new Error('execCommand copy failed')
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy practice URL', err)
      toast.error('Could not copy to clipboard. Copy the URL manually.')
    }
  }

  useForwardAction({
    label: completeOnboarding.isPending ? 'Finishing…' : 'Go live',
    disabled: completeOnboarding.isPending,
    onClick: handleGoLive,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Go live</h2>
        <p className="mt-1 text-sm text-slate-600">
          Review your setup below. When you&apos;re ready, go live and start seeing
          patients.
        </p>
      </div>

      {practiceUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your practice URL</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-slate-100 px-3 py-2 font-mono text-sm text-slate-800">
                {practiceUrl}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleCopy}
                aria-label="Copy practice URL"
              >
                <Copy className="mr-1 h-4 w-4" />
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Share this with patients to book visits.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Setup checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChecklistItem complete={accountDone} label="Account created" />
          <ChecklistItem complete={credentialsDone} label="Credentials verified" />
          <ChecklistItem complete={agreementsDone} label="Agreements signed" />
          <ChecklistItem
            complete={stripeInfo.complete}
            warning={stripeInfo.warning}
            label="Stripe Connect"
            detail={stripeInfo.detail}
            action={
              stripeInfo.warning
                ? { label: 'Finish in Stripe', onClick: () => goToStep(3) }
                : undefined
            }
          />
          <ChecklistItem complete={practiceDone} label="Practice profile complete" />
          <ChecklistItem
            complete={providerProfileDone}
            label="Provider profile complete"
          />
          <ChecklistItem
            complete={visitTypesDone}
            label="Visit types configured"
            detail={
              visitTypesDone
                ? `${activeVisitTypeCount} active visit type${activeVisitTypeCount === 1 ? '' : 's'}.`
                : undefined
            }
          />
          <ChecklistItem complete={availabilityDone} label="Availability set" />
          <ChecklistItem complete={protocolDone} label="Protocol acknowledged" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Next steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-600">
          <p>After going live you&apos;ll land on your visit queue in the provider portal.</p>
          <p>
            Share your practice URL with patients to start receiving bookings. You can
            adjust availability, visit types, and your profile anytime from Settings.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
